import { SplitDecision, TreePath } from './types';
import { RatioColumn } from '../parameters-schema';
import {
  formatRatioDescription,
  formatRatioPercent,
  formatRatioPercentInput
} from './ratio-utils';

/**
 * Generic function to extract paths from any decision tree
 */
export const extractPaths = <T>(
  node: any,
  currentPath: (SplitDecision | null)[] = [],
  maxDepth: number = 10,
  isLeafNode: (node: any) => boolean,
  getOutput: (node: any) => T,
  getChildren: (node: any) => { decision: SplitDecision; child: any }[]
): TreePath<T>[] => {
  if (currentPath.length >= maxDepth) {
    return [];
  }

  if (isLeafNode(node)) {
    return [{
      decisions: [...currentPath],
      output: getOutput(node)
    }];
  }

  const paths: TreePath<T>[] = [];
  const children = getChildren(node);

  children.forEach(({ decision, child }) => {
    const newPath = [...currentPath];
    // Ensure the path array is large enough for the columnIndex
    while (newPath.length <= node.split.columnIndex) {
      newPath.push(null);
    }
    newPath[node.split.columnIndex] = decision;
    paths.push(...extractPaths(child, newPath, maxDepth, isLeafNode, getOutput, getChildren));
  });

  return paths;
};

/**
 * Helper function to determine if cells can be merged vertically
 */
export const getMergeCount = <T>(
  paths: TreePath<T>[], 
  rowIndex: number, 
  colIndex: number
): number => {
  if (rowIndex >= paths.length) return 0;
  
  const currentPath = paths[rowIndex];
  let mergeCount = 1;
  
  // Look ahead to see how many consecutive rows have the same decision
  // AND the same decisions in all previous columns
  for (let i = rowIndex + 1; i < paths.length; i++) {
    const nextPath = paths[i];
    
    // Check if all decisions up to and including the current column are identical
    let canMerge = true;
    for (let j = 0; j <= colIndex; j++) {
      if (JSON.stringify(currentPath.decisions[j]) !== JSON.stringify(nextPath.decisions[j])) {
        canMerge = false;
        break;
      }
    }
    
    if (canMerge) {
      mergeCount++;
    } else {
      break;
    }
  }
  
  return mergeCount;
};

/**
 * Helper function to format decisions for display
 */
const getRatioDisplayName = (column: RatioColumn): string => {
  return formatRatioDescription(column);
};

export const formatDecision = (
  decision: SplitDecision | null,
  column?: any
): string => {
  if (!decision) return '—';
  
  switch (decision.type) {
    case 'numerical':
      if (decision.range === 'default') {
        return '> (all previous)';
      }
      if (column?.type === 'ratio') {
        return `≤ ${formatRatioPercent(decision.range.maxValue)}`;
      }
      return `≤ ${decision.range.maxValue}`;
    case 'categorical':
      return decision.category === 'default' 
        ? '(other)' 
        : decision.category;
    case 'boolean':
      return decision.value === true 
        ? 'true' 
        : 'default (false)';
    default:
      return '—';
  }
};

/**
 * Helper function to get column display name
 */
export const getColumnDisplayName = (column: any, tags?: any[], parameters?: any[]): string => {
  switch (column.type) {
    case 'numerical':
    case 'categorical':
      // Check if this is a DOB column that should show age transformation
      if (column.source === 'surveyAnswers' && column.path === 'dob' && column.type === 'numerical') {
        return column.displayName || 'Date of Birth (Age in Years)';
      }
      return column.displayName;
    case 'ratio':
      return getRatioDisplayName(column);
    case 'derived':
      return column.displayName;
    case 'lightStatus': {
      // Find the parameter by ID to get its display name
      const parameter = parameters?.find(p => p.id === column.parameterName);
      const displayName = parameter?.displayTitle || parameter?.id || column.parameterName;
      return `Light Status (${displayName})`;
    }
    case 'tags': {
      // Find the tag by ID to get its display name
      const tag = tags?.find(t => t.id === column.tagID);
      const displayName = tag?.displayName || tag?.id || column.tagID;
      return `Has Tag (${displayName})`;
    }
    default:
      return 'Unknown Column';
  }
};

/**
 * Helper function to check if a decision is a default condition
 */
export const isDefaultDecision = (decision: SplitDecision | null): boolean => {
  if (!decision) return true;
  
  switch (decision.type) {
    case 'numerical':
      return decision.range === 'default';
    case 'categorical':
      return decision.category === 'default';
    case 'boolean':
      return decision.value === false; // The false path is treated as default
    default:
      return true;
  }
};

/**
 * Helper function to get the current value of a decision
 */
export const getCurrentValue = (decision: SplitDecision | null, column?: any): string => {
  if (!decision) return '';
  
  switch (decision.type) {
    case 'numerical':
      if (decision.range === 'default') {
        return '';
      }
      if (column?.type === 'ratio') {
        return formatRatioPercentInput(decision.range.maxValue);
      }
      return decision.range.maxValue.toString();
    case 'categorical':
      return decision.category === 'default' ? '' : decision.category;
    case 'boolean':
      return decision.value === true ? 'true' : '';
    default:
      return '';
  }
};

/**
 * Get available options for a discrete column (categorical, lightStatus, tags)
 */
export const getAvailableOptions = (
  column: any,
  usedOptions: string[],
  tags?: any[],
  parameters?: any[]
): string[] => {
  switch (column.type) {
    case 'categorical':
      // For survey questions, get options from the question definition
      if (column.source === 'surveyAnswers') {
        // Import survey questions dynamically to avoid circular dependencies
        try {
          const { surveyQuestions } = require('../../../components/kyc-editor/question-types');
          const question = surveyQuestions[column.path];
          if (question && question.options) {
            return question.options.filter((option: string) => !usedOptions.includes(option));
          }
        } catch (error) {
          console.warn('Could not load survey questions:', error);
        }
      }
      return [];
      
    case 'lightStatus':
      // Light status has fixed options
      const lightStatusOptions = ['green', 'yellow', 'red'];
      return lightStatusOptions.filter(option => !usedOptions.includes(option));
      
    case 'tags':
      // For boolean tags, only true/false are available
      return ['true', 'false'].filter(option => !usedOptions.includes(option));
      
    default:
      return [];
  }
};

/**
 * Get used options for a specific column in a decision tree
 * Only checks within the current node, not across all branches
 */
export const getUsedOptionsForColumn = (node: any, columnIndex: number): string[] => {
  const usedOptions: string[] = [];
  
  if (!node || node.type === 'issueFlaggerLeaf' || node.type === 'strategyRecommendationLeaf') {
    return usedOptions;
  }
  
  // Only check the current node's splits for the specified column
  if (node.split && node.split.columnIndex === columnIndex) {
    switch (node.type) {
      case 'categoricalSplit':
        if (node.split.categoryMap) {
          node.split.categoryMap.forEach((cat: any) => {
            if (cat.category !== 'default') {
              usedOptions.push(cat.category);
            }
          });
        }
        break;
        
      case 'booleanSplit':
        // For boolean splits, we consider both true and false as "used"
        usedOptions.push('true', 'false');
        break;
        
      case 'lightStatusSplit':
        // For light status splits, check what statuses are already used
        if (node.split.statusMap) {
          node.split.statusMap.forEach((status: any) => {
            if (status.status !== 'default') {
              usedOptions.push(status.status);
            }
          });
        }
        break;
    }
  }
  
  return usedOptions;
};

/**
 * Get the remaining option for discrete columns when only one is left
 */
export const getRemainingOption = (
  column: any,
  usedOptions: string[],
  tags?: any[],
  parameters?: any[]
): string | null => {
  const availableOptions = getAvailableOptions(column, usedOptions, tags || [], parameters || []);
  
  if (availableOptions.length === 1) {
    return availableOptions[0];
  }
  
  return null;
};

/**
 * Get used options for a specific column at a specific path in the decision tree
 * Only checks within the node at the specified path, not across all branches
 */
export const getUsedOptionsForColumnAtPath = (node: any, targetPath: any, columnIndex: number): string[] => {
  if (!targetPath || !targetPath.decisions) {
    // If no target path, check the root node
    return getUsedOptionsForColumn(node, columnIndex);
  }
  
  // Navigate to the parent node of the target path (the node that will contain the new split)
  let currentNode = node;
  for (let i = 0; i < targetPath.decisions.length - 1; i++) {
    const decision = targetPath.decisions[i];
    if (!decision || !currentNode.split) {
      break;
    }
    
    switch (decision.type) {
      case 'numerical':
        if (decision.range === 'default') {
          currentNode = currentNode.split.defaultChild;
        } else {
          const range = currentNode.split.ranges?.find((r: any) => r.maxValue === decision.range.maxValue);
          currentNode = range?.childNode;
        }
        break;
      case 'categorical':
        if (decision.category === 'default') {
          currentNode = currentNode.split.defaultChild;
        } else {
          const category = currentNode.split.categoryMap?.find((c: any) => c.category === decision.category);
          currentNode = category?.childNode;
        }
        break;
      case 'boolean':
        if (decision.value === true) {
          currentNode = currentNode.split.trueChild;
        } else {
          currentNode = currentNode.split.falseChild;
        }
        break;
    }
    
    if (!currentNode) {
      break;
    }
  }
  
  // Now check used options in this parent node
  return getUsedOptionsForColumn(currentNode, columnIndex);
};
